/**
 * Readable text for a column filter's value — what the filter's own
 * trigger shows in the filter row, reused by the toolbar's filter pill
 * so both say the same thing about the same filter.
 *
 * One reader per filter type, keyed on the column's `meta.qhsFilter`:
 *   - select / checkbox: option labels for the selected values;
 *   - select with dynamic hint: the values as typed, with an excluded
 *     side prefixed "not";
 *   - date: the day, or a range written "start – end";
 *   - text / currency / price: the value as entered.
 */
import FILTER_TYPES from '../Filters/constants';
import { getTitle } from '../Filters/SelectWithDynamicHintFilter/helpers';
import { toSplitFilterValue } from '../../../lib/filterValue';

export { hasFilterValue } from '../../../lib/filterValue';

/**
 * A select filter's value arrives as an array or a comma-separated
 * string; reads either as an array of selected values, `[]` when empty.
 */
export const normaliseSelectValue = value => {
  if (Array.isArray(value)) return value.filter(v => v !== '');
  if (value === '' || value == null) return [];
  if (typeof value === 'string') return value.split(',').filter(Boolean);
  return [];
};

/** Comma-joined option labels; a value with no matching option shows as is. */
export const describeSelectValue = (values, options = []) =>
  values.map(v => options.find(o => o.value === v)?.label || v).join(', ');

/**
 * Include and exclude sides of an advanced filter, joined with " · ".
 * The excluded side has to say so, or an exclusion reads as its own
 * opposite. Empty string when nothing is selected on either side.
 *
 * `dict` carries the translation groups `getTitle` maps stored keys
 * through: `{ scit, slit, formulas, tables }`.
 */
export const describeSplitValue = (value, dict) => {
  const { include, exclude } = toSplitFilterValue(value || []);
  const parts = [];
  if (include.length) parts.push(getTitle(include, dict));
  if (exclude.length) {
    parts.push(`${dict?.tables?.excludePrefix || 'not'} ${getTitle(exclude, dict)}`);
  }
  return parts.join(' · ');
};

// A range is stored as "MM/DD/YYYY,MM/DD/YYYY".
const describeDateValue = value => String(value).split(',').filter(Boolean).join(' – ');

/** Readable value of one `{ id, value }` filter entry for its column. */
export const describeFilterValue = (value, filterMeta, dict) => {
  switch (filterMeta?.filterType) {
    case FILTER_TYPES.SELECT:
    case FILTER_TYPES.CHECKBOX:
      return describeSelectValue(normaliseSelectValue(value), filterMeta.options);
    case FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT:
      return describeSplitValue(value, dict);
    case FILTER_TYPES.DATE:
      return describeDateValue(value);
    default:
      return Array.isArray(value) ? value.join(', ') : String(value ?? '');
  }
};
